import { useEffect, useState } from "react";
import { isCarePremise, type PremiseConfig } from "@shared/types/foyer";
import { withGlobalStations } from "@shared/radio-merge";
import { withGlobalApps } from "@shared/app-merge";
import {
  foyerApi,
  type AppRecord,
  type RadioStationRecord,
} from "../http/foyer";

/**
 * Build the config the way the server serves it for rendering: the global radio
 * + app catalogues merged with this premise's custom/disabled lists. Used by
 * both the inline live preview (iframe) and the "Open preview" popup so the two
 * match the real TV screen. Null-safe (returns null until a premise exists).
 */
export function usePreviewConfig(
  premise: PremiseConfig | null
): PremiseConfig | null {
  const [stations, setStations] = useState<RadioStationRecord[]>([]);
  const [apps, setApps] = useState<AppRecord[]>([]);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listRadioStations()
      .then((list) => alive && setStations(Array.isArray(list) ? list : []))
      .catch(() => {});
    foyerApi
      .listApps()
      .then((list) => alive && setApps(Array.isArray(list) ? list : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!premise) return null;
  if (!premise.scheme) return premise;
  return {
    ...premise,
    scheme: {
      ...premise.scheme,
      content: {
        ...premise.scheme.content,
        apps: withGlobalApps(
          apps,
          premise.scheme.content.disabledAppIds,
          premise.scheme.content.appOrder,
          // The preview must show what the television will (NFLX-6).
          isCarePremise({ type: premise.type, segment: premise.segment })
        ),
        stations: withGlobalStations(
          stations,
          premise.scheme.content.stations,
          premise.scheme.content.disabledStationIds,
          premise.scheme.content.stationOrder
        ),
      },
    },
  };
}
